/* Byte-Size site content — services, hero takes, guarantee options.
   Plain JS so every page (and the nav dropdown) reads one source of truth. */

export const services = [
  {
    id: 'ai-education',
    name: 'AI education & training',
    short: 'AI training',
    card: 'Plain-English training for you and your team.',
    blurb: 'AI is useful the way a ute is useful — once someone shows you how to drive it. We run hands-on sessions at your workplace, on your real work: writing quotes, answering emails, summarising the stuff nobody has time to read.',
    intro: 'No hype, no theory, no 47-slide deck about the future. We sit with you and your team, take real jobs off your desk, and show you where AI genuinely helps — and where it doesn\'t.',
    symptoms: [
      'Everyone\'s talking about AI and you don\'t know where to start',
      'The team already uses it quietly, with no ground rules',
      'You tried it once, it made something up, and you gave up'
    ],
    actions: [
      'Hands-on training on your actual work, not canned demos',
      'Plain rules for what\'s safe to put in — and what never is',
      'Cheat sheets the team keeps at their desks',
      'A follow-up visit once it\'s had time to stick'
    ],
    cta: 'Want your team confident with AI?'
  },
  {
    id: 'ai-transformation',
    name: 'AI transformation',
    short: 'AI at work',
    card: 'Put AI to work on your actual jobs.',
    blurb: 'Not a strategy deck — working changes. We find the two or three jobs in your week that AI can genuinely take over, wire it into the tools you already use, and measure the hours it gives back.',
    intro: 'The goal isn\'t "AI in your business". The goal is your Tuesday afternoons back. We start small, prove it works, and only then go further.',
    symptoms: [
      'Quoting, replying and paperwork eat your evenings',
      'You suspect AI could help, but can\'t point at where',
      'You\'re worried about customer data ending up somewhere it shouldn\'t'
    ],
    actions: [
      'Find the tasks actually worth automating — usually 2 or 3',
      'Set it up inside the tools you already use',
      'Ground rules so customer data stays put',
      'Check back and measure the hours saved'
    ],
    cta: 'Curious what AI could take off your plate?'
  },
  {
    id: 'shopify-ecommerce',
    name: 'Shopify & e-commerce',
    short: 'Shopify',
    card: 'Store fixes, plugins and the missing 10%.',
    blurb: 'Most stores do 90% of what the owner needs, and the last 10% gets done by hand every night. We fix broken things, build the small custom bits, and connect the store to the rest of your business.',
    intro: 'You shouldn\'t need three subscriptions and a spare evening to run one shop. We make your store do the whole job.',
    symptoms: [
      'There\'s "an app for that", but it\'s $79 a month for one small feature',
      'Orders get retyped into accounting or the label printer',
      'Shipping rules, variants or stock counts do something weird'
    ],
    actions: [
      'Fix store problems and checkout oddities',
      'Build small plugins that do exactly your thing — no monthly fee',
      'Connect the store to accounting, stock and shipping',
      'Tidy up the theme without a full redesign'
    ],
    cta: 'Store almost doing what you need?'
  },
  {
    id: 'systems-integration',
    name: 'Making programs talk',
    short: 'making programs talk',
    card: 'One entry, everywhere it needs to go.',
    blurb: 'The booking system doesn\'t know what the spreadsheet knows, so a human plays messenger every day. We wire your existing tools together so information moves by itself.',
    intro: 'Your programs are perfectly capable of talking to each other. Nobody\'s ever introduced them. We do introductions.',
    symptoms: [
      'The same data gets typed into two or three systems',
      'Every week someone exports a CSV, fixes it in Excel, imports it somewhere else',
      'Sales live in one program, stock in another — and they disagree'
    ],
    actions: [
      'Connect the tools you already own — no rip-and-replace',
      'Make the syncing automatic and boring',
      'One source of truth for the numbers that matter',
      'Leave you a plain-English map of how it all connects'
    ],
    cta: 'Tired of playing messenger between programs?'
  },
  {
    id: 'websites',
    name: 'Websites',
    short: 'Websites',
    card: 'Fixes, tune-ups and hosting — no rebuild required.',
    blurb: 'Most website jobs aren\'t rebuilds. They\'re "it\'s down", "it\'s slow", "I can\'t log in", and "the person who built it moved overseas". We handle exactly those.',
    intro: 'A small business website should be boring: always up, easy to change, cheap to run. We get yours there and keep it there.',
    symptoms: [
      'It\'s down and you don\'t know who to call',
      'It was built by someone who\'s no longer around',
      'Renewal emails keep arriving and you\'re not sure which are real'
    ],
    actions: [
      'Emergency fixes when it\'s down or hacked',
      'Speed-ups, small changes and content updates',
      'Untangle domains, hosting and renewals into one tidy list',
      'Show you how to make small updates yourself'
    ],
    cta: 'Website misbehaving?'
  },
  {
    id: 'automation',
    name: 'Automation',
    short: 'Automation',
    card: 'Kill the repetitive manual work.',
    blurb: 'If you do the same clicks every Monday, a computer should be doing them. We watch how the job really flows, then automate the boring middle of it.',
    intro: 'Automation isn\'t robots. It\'s your Monday morning, two hours shorter.',
    symptoms: [
      'Invoices get typed out of emails, by a person',
      'There\'s a weekly copy-paste ritual everyone dreads',
      'Reminders and reports go out by hand — when someone remembers'
    ],
    actions: [
      'Sit with you and map the repetitive job as it really happens',
      'Automate the middle, keep humans on the judgement calls',
      'Use small, cheap tools — not another platform subscription',
      'Write it down so it doesn\'t become the new mystery'
    ],
    cta: 'Got a job you\'re sick of doing by hand?'
  },
  {
    id: 'data-reporting',
    name: 'Data & reporting',
    short: 'Data & reports',
    card: 'Dashboards and spreadsheets that tell the truth.',
    blurb: 'You already have the numbers — they\'re just in four places, and they disagree. We pull them into one honest view so decisions stop being guesswork.',
    intro: 'Good decisions need numbers you trust. That\'s the whole service.',
    symptoms: [
      'The numbers in two systems never quite match',
      'There\'s one giant spreadsheet only one person understands',
      'You decide on gut feel because the real report takes a week'
    ],
    actions: [
      'One source of truth for the numbers you actually use',
      'A simple dashboard: sales, cash, stock — no clutter',
      'Rebuild the scary spreadsheet so anyone can drive it',
      'Move off spreadsheets entirely when that\'s the honest answer'
    ],
    cta: 'Want numbers you can trust?'
  },
  {
    id: 'problem-solving',
    name: 'Something\'s broken',
    short: 'Something\'s broken',
    card: 'Can\'t name the problem? Start here.',
    blurb: 'Half our jobs don\'t fit a category: the printer and the laptop have stopped being friends, the email "just stopped", two systems blame each other. Bring it here.',
    intro: 'You don\'t need to know if it\'s a website thing, a Wi-Fi thing or a spreadsheet thing. Naming the problem is our job — you just have to point at it.',
    symptoms: [
      'The thing won\'t do the thing, and you\'ve already turned it off and on again',
      'Two suppliers each say it\'s the other one\'s fault',
      'It works on one computer and not the other, and nobody knows why'
    ],
    actions: [
      'Diagnose across the whole tangle, not one box at a time',
      'Fix it — or find the person who can, and translate',
      'Explain what happened in plain English',
      'Leave it more reliable than we found it'
    ],
    cta: 'Got a mystery?'
  }
];

export const heroTakes = {
  brand:   { pre: 'Byte-size solutions for ', hl: 'small business', post: ' problems.' },
  problem: { pre: 'You bring the problem. ', hl: 'We bring the solution.', post: '' },
  fixed:   { pre: 'Whatever\'s broken, ', hl: 'consider it solved', post: '.' }
};

export const guarantees = [
  'We don\'t fix it, you don\'t pay.',
  'No fix, no fee.',
  'If we can\'t fix it, it\'s free.'
];
